import { useEffect, useState, type ReactElement } from 'react';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { fetchContentPage, type ContentPage } from '../lib/contentClient';
import { renderMarkdown } from '../lib/markdown';

interface ContentPageViewProps {
  slug: string;
  /** Rendered if the slug is missing, unpublished, or the fetch fails. */
  fallback: () => ReactElement;
}

export function ContentPageView({ slug, fallback }: ContentPageViewProps) {
  const [state, setState] = useState<
    { status: 'loading' } | { status: 'ready'; page: ContentPage } | { status: 'missing' }
  >({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    fetchContentPage(slug)
      .then((page) => {
        if (cancelled) return;
        setState(page ? { status: 'ready', page } : { status: 'missing' });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'missing' });
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Always set a stable title until the fetch resolves so screen readers
  // don't see a flash of "Skiffs" then the real title.
  const title = state.status === 'ready' ? `${state.page.title} — Skiffs` : 'Skiffs';
  useDocumentTitle(title);

  if (state.status === 'loading') {
    return (
      <article className="skf-card" aria-busy="true">
        <p className="skf-meta">Loading…</p>
      </article>
    );
  }

  if (state.status === 'missing') {
    return fallback();
  }

  const html = renderMarkdown(state.page.body_md);

  return (
    <article className="skf-card">
      <h1 className="skf-title">{state.page.title}</h1>
      {state.page.subtitle ? <p className="skf-meta">{state.page.subtitle}</p> : null}
      <div
        className="skf-stack"
        style={{ marginTop: 24 }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </article>
  );
}
